import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Fade, Slide } from '@wds/motion';
import { safeRest, KEYS, useIsomorphicLayoutEffect, isBrowser } from '@wds/utils';
import { forwardRef, useEffect, createRef } from 'react';
import { Overlay, CloseButton } from '@wds/internal-components';
import { createMemoStyles } from '@wds/styles';
import { Responsive } from '@wds/responsive';
import { CSSTransition } from 'react-transition-group';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { IconOpenMinimizeRight, IconOpenMinimizeLeft, IconX } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var SHEET_SIZES = ['sm', 'md', 'lg'];
var SHEET_PLACEMENTS = ['left', 'right', 'bottom'];
var SHEET_WIDTHS = {
  sm: '360px',
  md: '420px',
  lg: '640px'
};

var useStyles$5 = createMemoStyles(function (theme) {
  var _a;
  var palette = theme.palette;
  var overlayShadow = {
    left: theme.shadow.left,
    right: theme.shadow.right,
    bottom: theme.shadow.bottom
  };
  var sheetStyles = {
    wrenchSheetOverlayContainer: {
      position: 'relative',
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      }
    },
    wrenchSheetOverlay: {
      '& > div': {
        position: 'fixed',
        top: function (_a) {
          var offsetTop = _a.offsetTop;
          return offsetTop ? "".concat(offsetTop, "px") : 0;
        },
        left: '0',
        right: '0',
        bottom: '0',
        backgroundColor: palette.background.overlay
      }
    },
    wrenchSheetContainer: {
      position: 'fixed',
      top: function (_a) {
        var placement = _a.placement;
        return placement !== 'bottom' ? 0 : null;
      },
      bottom: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? 0 : null;
      },
      left: 0,
      right: 0,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      }
    },
    wrenchSheet: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      top: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? 0 : null;
      },
      bottom: function (_a) {
        var placement = _a.placement;
        return placement !== 'bottom' ? 0 : null;
      },
      pointerEvents: 'none'
    },
    wrenchSheetPanel: (_a = {
      backgroundColor: palette.background.light,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space + 1;
      },
      display: 'flex',
      flexDirection: 'column',
      minWidth: SHEET_WIDTHS.sm,
      overflowY: 'auto',
      outline: 'none',
      pointerEvents: 'all',
      width: function (_a) {
        var placement = _a.placement,
          size = _a.size;
        return placement !== 'bottom' && size ? SHEET_WIDTHS[size] : null;
      },
      height: function (_a) {
        var placement = _a.placement,
          offsetTop = _a.offsetTop;
        return placement === 'bottom' ? 'auto' : "calc(100vh - ".concat(offsetTop ? "".concat(offsetTop.toString(), "px") : '0px', ")");
      },
      minHeight: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? '42px' : null;
      },
      position: function (_a) {
        var placement = _a.placement;
        return placement ? 'fixed' : null;
      },
      bottom: function (_a) {
        var placement = _a.placement;
        return placement ? 0 : null;
      },
      top: function (_a) {
        var placement = _a.placement,
          offsetTop = _a.offsetTop;
        return placement !== 'bottom' ? offsetTop ? "".concat(offsetTop.toString(), "px") : '0px' : null;
      },
      left: function (_a) {
        var placement = _a.placement;
        return placement !== 'right' ? 0 : null;
      },
      right: function (_a) {
        var placement = _a.placement;
        return placement !== 'left' ? 0 : null;
      },
      boxShadow: function (_a) {
        var placement = _a.placement;
        return placement ? overlayShadow[placement] : null;
      }
    }, _a["@media (max-width: ".concat(SHEET_WIDTHS['sm'], ")")] = {
      width: function (_a) {
        var size = _a.size;
        return size === 'sm' ? '100%' : null;
      },
      minWidth: function (_a) {
        var size = _a.size;
        return size === 'sm' ? 0 : null;
      }
    }, _a["@media (max-width: ".concat(SHEET_WIDTHS['md'], ")")] = {
      width: function (_a) {
        var size = _a.size;
        return size === 'md' ? '100%' : null;
      },
      minWidth: function (_a) {
        var size = _a.size;
        return size === 'md' ? 0 : null;
      }
    }, _a["@media (max-width: ".concat(SHEET_WIDTHS['lg'], ")")] = {
      width: function (_a) {
        var size = _a.size;
        return size === 'lg' ? '100%' : null;
      },
      minWidth: function (_a) {
        var size = _a.size;
        return size === 'lg' ? 0 : null;
      }
    }, _a)
  };
  return sheetStyles;
});

var useStyles$4 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  return {
    wrenchSheetFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: [spacing[4], spacing[5]],
      background: palette.background.light,
      borderTop: "1px solid ".concat(palette.border.muted),
      color: palette.text.dark
    },
    wrenchSheetActions: {
      display: 'flex',
      flexGrow: 1,
      flexWrap: 'wrap',
      justifyContent: function (_a) {
        var align = _a.align;
        return align === 'left' ? 'flex-start' : 'flex-end';
      },
      '&:not([class^="wrenchButtonGroup"])': {
        '& > button, & > a': {
          marginLeft: function (_a) {
            var align = _a.align;
            return align === 'right' ? spacing[4] : undefined;
          },
          marginRight: function (_a) {
            var align = _a.align;
            return align === 'left' ? spacing[4] : undefined;
          }
        }
      }
    }
  };
});

var SheetFooter = function SheetFooter(_a) {
  var _b = _a.align,
    align = _b === void 0 ? 'left' : _b,
    children = _a.children;
  var _c = useStyles$4({
      align: align
    }),
    wrenchSheetActions = _c.wrenchSheetActions,
    wrenchSheetFooter = _c.wrenchSheetFooter;
  return jsx("footer", {
    className: wrenchSheetFooter,
    children: jsx("div", {
      className: wrenchSheetActions,
      children: children
    })
  });
};

var useStyles$3 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  var SheetBodyStyles = {
    wrenchSheetBody: {
      overflowY: 'auto',
      flexGrow: 1,
      padding: function (_a) {
        var padding = _a.padding;
        return padding ? [[spacing[4], spacing[5], spacing[5], spacing[5]]] : undefined;
      }
    }
  };
  return SheetBodyStyles;
});

var SheetBody = /*#__PURE__*/forwardRef(function (_a, ref) {
  _a.className;
    var children = _a.children,
    handleClick = _a.handleClick,
    id = _a.id,
    _b = _a.padding,
    padding = _b === void 0 ? true : _b,
    rest = __rest(_a, ["className", "children", "handleClick", "id", "padding"]);
  var wrenchSheetBody = useStyles$3({
    padding: padding
  }).wrenchSheetBody;
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === KEYS.ESC) {
      handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
      e.preventDefault();
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsx("section", __assign({
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      ref: ref,
      id: id,
      className: wrenchSheetBody,
      onKeyDown: handleKeyDown
    }, safeRest(rest), {
      children: children
    }))
  );
});
SheetBody.displayName = 'SheetBody';

var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize;
  return {
    wrenchSheetHeader: function () {
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: [spacing[4], spacing[5], spacing[4], spacing[5]],
        background: palette.background.light,
        borderBottom: "1px solid ".concat(palette.border.muted),
        flexShrink: 0,
        flexDirection: 'column'
      };
    },
    wrenchSheetText: {
      fontSize: 20,
      fontWeight: fontWeight.medium,
      color: palette.text.dark,
      marginTop: 0,
      marginBottom: 0
    },
    wrenchSheetTitle: {
      flexGrow: 1,
      width: '100%',
      display: 'flex'
    },
    wrenchSheetTitleHeaders: {
      flexGrow: 1
    },
    wrenchSheetTitleInteractiveAddon: {
      flex: '0 0',
      marginRight: spacing[2]
    },
    wrenchSheetSubText: {
      fontSize: fontSize.bodySm,
      fontWeight: fontWeight.regular,
      color: palette.text.darkMuted,
      margin: [spacing[2], 0, 0]
    },
    wrenchSheetAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});

var SheetHeader = function SheetHeader(_a) {
  var addOn = _a.addOn,
    interactiveAddon = _a.interactiveAddon,
    header = _a.header,
    id = _a.id,
    subHeader = _a.subHeader;
  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }
  var _b = useStyles$2(),
    wrenchSheetAddOn = _b.wrenchSheetAddOn,
    wrenchSheetHeader = _b.wrenchSheetHeader,
    wrenchSheetSubText = _b.wrenchSheetSubText,
    wrenchSheetText = _b.wrenchSheetText,
    wrenchSheetTitle = _b.wrenchSheetTitle,
    wrenchSheetTitleHeaders = _b.wrenchSheetTitleHeaders,
    wrenchSheetTitleInteractiveAddon = _b.wrenchSheetTitleInteractiveAddon;
  return jsxs("section", {
    className: wrenchSheetHeader,
    children: [jsxs("div", {
      className: wrenchSheetTitle,
      children: [interactiveAddon && jsx("div", {
        className: wrenchSheetTitleInteractiveAddon,
        children: interactiveAddon
      }), jsxs("div", {
        className: wrenchSheetTitleHeaders,
        children: [header && jsx("h2", {
          className: wrenchSheetText,
          id: id,
          children: header
        }), subHeader && jsx("p", {
          className: wrenchSheetSubText,
          children: subHeader
        })]
      })]
    }), addOn && jsx("div", {
      className: wrenchSheetAddOn,
      children: addOn
    })]
  });
};

var Sheet = function Sheet(_a) {
  var children = _a.children,
    onClose = _a.onClose,
    _b = _a.show,
    show = _b === void 0 ? false : _b,
    _c = _a.size,
    size = _c === void 0 ? 'sm' : _c,
    _d = _a.placement,
    placement = _d === void 0 ? 'right' : _d,
    _e = _a.showCloseButton,
    showCloseButton = _e === void 0 ? false : _e,
    _f = _a.closeButtonLabel,
    closeButtonLabel = _f === void 0 ? 'Close Sheet' : _f,
    _g = _a.blocking,
    blocking = _g === void 0 ? true : _g,
    _h = _a.offsetTop,
    offsetTop = _h === void 0 ? 0 : _h,
    elevation = _a.elevation,
    _j = _a.alwaysRenderContent,
    alwaysRenderContent = _j === void 0 ? false : _j,
    rest = __rest(_a, ["children", "onClose", "show", "size", "placement", "showCloseButton", "closeButtonLabel", "blocking", "offsetTop", "elevation", "alwaysRenderContent"]);
  var sheetPanelRef = /*#__PURE__*/createRef();
  var classes = useStyles$5({
    size: size,
    placement: placement,
    blocking: blocking,
    offsetTop: offsetTop,
    elevation: elevation
  });
  useIsomorphicLayoutEffect(function () {
    if (isBrowser && blocking) {
      if (show) {
        document.body.style.overflowY = 'hidden';
      } else {
        document.body.style.overflowY = 'auto';
      }
    }
    return function () {
      if (isBrowser && blocking) {
        document.body.style.overflowY = 'auto';
      }
    };
  });
  useEffect(function () {
    var _a;
    if (show) {
      (_a = sheetPanelRef === null || sheetPanelRef === void 0 ? void 0 : sheetPanelRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [show]);
  var getSlideDirection = function getSlideDirection() {
    var direction = 'left';
    if (!placement || placement === 'right') {
      return direction;
    }
    return placement === 'bottom' ? 'up' : 'right';
  };
  var handleClick = function handleClick() {
    onClose === null || onClose === void 0 ? void 0 : onClose();
  };
  var closeButton = function closeButton() {
    return jsx(CloseButton, {
      onClick: handleClick,
      variant: "dark",
      label: closeButtonLabel
    });
  };
  return jsxs(Fragment, {
    children: [blocking && jsx("div", {
      className: classes.wrenchSheetOverlayContainer,
      children: jsx(Fade, {
        in: show,
        speed: "fast",
        alwaysRenderContent: alwaysRenderContent,
        children: jsx("div", {
          className: classes.wrenchSheetOverlay,
          children: jsx(Overlay, {
            onClick: handleClick,
            className: "OverlayWDS"
          })
        })
      })
    }), jsx("div", {
      className: classes.wrenchSheetContainer,
      children: jsx(Slide, {
        in: show,
        direction: getSlideDirection(),
        alwaysRenderContent: alwaysRenderContent,
        children: jsx("div", __assign({
          className: classes.wrenchSheet
        }, safeRest(rest), {
          children: jsxs("div", {
            role: "dialog",
            className: classes.wrenchSheetPanel,
            ref: sheetPanelRef,
            tabIndex: -1,
            children: [showCloseButton && closeButton(), children]
          })
        }))
      })
    })]
  });
};
Sheet.Header = SheetHeader;
Sheet.Body = SheetBody;
Sheet.Footer = SheetFooter;

var useStyles$1 = createMemoStyles(function (theme) {
  return {
    wrenchInlineSheetWrapper: {
      width: '100%',
      height: '100%',
      overflowY: function (_a) {
        var overflowYHidden = _a.overflowYHidden;
        return overflowYHidden ? 'hidden' : undefined;
      }
    }
  };
});

var InlineSheetWrapper = function InlineSheetWrapper(_a) {
  var _b = _a.overflowYHidden,
    overflowYHidden = _b === void 0 ? false : _b,
    children = _a.children;
  var wrenchInlineSheetWrapper = useStyles$1({
    overflowYHidden: overflowYHidden
  }).wrenchInlineSheetWrapper;
  return jsx(Box, {
    display: "flex",
    alignItems: "stretch",
    className: wrenchInlineSheetWrapper,
    children: children
  });
};

var getTransition = function getTransition(isEnter, transitionProperty, withOpacity) {
  if (withOpacity === void 0) {
    withOpacity = false;
  }
  var curveEntrance = 'cubic-bezier(0, 0, 0, 1)';
  var curveEase = 'cubic-bezier(0.2, 0, 0.1, 1)';
  var bezier = isEnter ? curveEntrance : curveEase;
  var transition = "".concat(transitionProperty, " ").concat(450, "ms ").concat(bezier);
  if (!withOpacity) {
    return transition;
  }
  return "".concat(transition, ", opacity ").concat(200, "ms linear");
};
var getExpandedWidth = function getExpandedWidth(size) {
  return size ? SHEET_WIDTHS[size] : SHEET_WIDTHS.sm;
};
var getMinimizedWidth = function getMinimizedWidth(showCollapsedSheet) {
  return showCollapsedSheet ? '64px' : '0px';
};
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette;
  var styles = {
    wrenchInlineSheetContainer: {
      overflow: 'hidden',
      flexShrink: 0,
      flexGrow: 0,
      background: 'white',
      borderLeft: "1px solid ".concat(palette.border.muted),
      position: 'relative',
      width: function (_a) {
        var show = _a.show,
          showCollapsedSheet = _a.showCollapsedSheet;
        return !show ? getMinimizedWidth(showCollapsedSheet) : null;
      }
    },
    wrenchInlineSheetContainerEnter: {
      width: function (_a) {
        var showCollapsedSheet = _a.showCollapsedSheet;
        return getMinimizedWidth(showCollapsedSheet);
      }
    },
    wrenchInlineSheetContainerEnterActive: {
      transition: getTransition(true, 'width', false),
      width: function (_a) {
        var size = _a.size;
        return getExpandedWidth(size);
      }
    },
    wrenchInlineSheetContainerEnterDone: {
      width: function (_a) {
        var size = _a.size;
        return getExpandedWidth(size);
      }
    },
    wrenchInlineSheetContainerExit: {
      width: function (_a) {
        var size = _a.size;
        return getExpandedWidth(size);
      }
    },
    wrenchInlineSheetContainerExitActive: {
      transition: getTransition(false, 'width', false),
      width: function (_a) {
        var showCollapsedSheet = _a.showCollapsedSheet;
        return getMinimizedWidth(showCollapsedSheet);
      }
    },
    wrenchInlineSheetContainerExitDone: {
      width: function (_a) {
        var showCollapsedSheet = _a.showCollapsedSheet;
        return getMinimizedWidth(showCollapsedSheet);
      }
    },
    wrenchInlineSheet: {
      width: function (_a) {
        var size = _a.size;
        return getExpandedWidth(size);
      },
      height: '100%'
    },
    wrenchInlineSheetEnter: {
      opacity: 0
    },
    wrenchInlineSheetEnterActive: {
      transition: getTransition(true, 'transform', true),
      opacity: 1
    },
    wrenchInlineSheetEnterDone: {
      opacity: 1
    },
    wrenchInlineSheetExit: {
      opacity: 1
    },
    wrenchInlineSheetExitActive: {
      transition: getTransition(false, 'transform', true),
      opacity: 0
    },
    wrenchInlineSheetExitDone: {
      opacity: 0
    }
  };
  return styles;
});

var useStylesInlineSheetButton = createMemoStyles({
  wrenchInlineSheetButton: {
    position: 'absolute',
    right: '16px',
    top: '16px'
  }
});

var InlineSheetButton = function InlineSheetButton(_a) {
  var show = _a.show,
    showCollapsedSheet = _a.showCollapsedSheet,
    showCloseButton = _a.showCloseButton,
    onToggle = _a.onToggle,
    closeButtonLabel = _a.closeButtonLabel,
    openButtonLabel = _a.openButtonLabel;
  var wrenchInlineSheetButton = useStylesInlineSheetButton().wrenchInlineSheetButton;
  if (!showCloseButton) {
    return null;
  }
  var icon;
  if (showCollapsedSheet) {
    if (show) {
      icon = jsx(IconOpenMinimizeRight, {
        size: "md",
        title: "collapse"
      });
    } else {
      icon = jsx(IconOpenMinimizeLeft, {
        size: "md",
        title: "expand"
      });
    }
  } else {
    icon = jsx(IconX, {
      size: "md",
      title: "close"
    });
  }
  return jsx("div", {
    className: wrenchInlineSheetButton,
    children: jsx(Button, {
      variant: "text",
      color: "alt",
      size: "sm",
      label: show ? closeButtonLabel : openButtonLabel,
      title: show ? closeButtonLabel : openButtonLabel,
      onClick: function () {
        return onToggle === null || onToggle === void 0 ? void 0 : onToggle(!show);
      },
      children: icon
    })
  });
};

var InlineSheet = function InlineSheet(_a) {
  var _b = _a.show,
    show = _b === void 0 ? false : _b,
    _c = _a.size,
    size = _c === void 0 ? 'sm' : _c,
    showCollapsedSheet = _a.showCollapsedSheet,
    _d = _a.showCloseButton,
    showCloseButton = _d === void 0 ? false : _d,
    _e = _a.closeButtonLabel,
    closeButtonLabel = _e === void 0 ? 'Close Sheet' : _e,
    _f = _a.openButtonLabel,
    openButtonLabel = _f === void 0 ? 'Open Sheet' : _f,
    onToggle = _a.onToggle,
    children = _a.children;
  var _g = useStyles({
      size: size,
      show: show,
      showCollapsedSheet: showCollapsedSheet
    }),
    wrenchInlineSheetContainer = _g.wrenchInlineSheetContainer,
    wrenchInlineSheetContainerEnter = _g.wrenchInlineSheetContainerEnter,
    wrenchInlineSheetContainerEnterActive = _g.wrenchInlineSheetContainerEnterActive,
    wrenchInlineSheetContainerEnterDone = _g.wrenchInlineSheetContainerEnterDone,
    wrenchInlineSheetContainerExit = _g.wrenchInlineSheetContainerExit,
    wrenchInlineSheetContainerExitActive = _g.wrenchInlineSheetContainerExitActive,
    wrenchInlineSheetContainerExitDone = _g.wrenchInlineSheetContainerExitDone,
    wrenchInlineSheet = _g.wrenchInlineSheet,
    wrenchInlineSheetEnter = _g.wrenchInlineSheetEnter,
    wrenchInlineSheetEnterActive = _g.wrenchInlineSheetEnterActive,
    wrenchInlineSheetEnterDone = _g.wrenchInlineSheetEnterDone,
    wrenchInlineSheetExit = _g.wrenchInlineSheetExit,
    wrenchInlineSheetExitActive = _g.wrenchInlineSheetExitActive,
    wrenchInlineSheetExitDone = _g.wrenchInlineSheetExitDone;
  return jsx(CSSTransition, {
    className: wrenchInlineSheetContainer,
    classNames: {
      enter: wrenchInlineSheetContainerEnter,
      enterActive: wrenchInlineSheetContainerEnterActive,
      enterDone: wrenchInlineSheetContainerEnterDone,
      exit: wrenchInlineSheetContainerExit,
      exitActive: wrenchInlineSheetContainerExitActive,
      exitDone: wrenchInlineSheetContainerExitDone
    },
    in: show,
    timeout: 450,
    children: jsxs("div", {
      children: [jsx(CSSTransition, {
        mountOnEnter: true,
        className: wrenchInlineSheet,
        classNames: {
          enter: wrenchInlineSheetEnter,
          enterActive: wrenchInlineSheetEnterActive,
          enterDone: wrenchInlineSheetEnterDone,
          exit: wrenchInlineSheetExit,
          exitActive: wrenchInlineSheetExitActive,
          exitDone: wrenchInlineSheetExitDone
        },
        timeout: 450,
        in: show,
        children: jsx(Box, {
          display: "flex",
          flexDirection: "column",
          children: children
        })
      }), jsx(InlineSheetButton, {
        show: show,
        onToggle: onToggle,
        showCollapsedSheet: showCollapsedSheet,
        showCloseButton: showCloseButton,
        closeButtonLabel: closeButtonLabel,
        openButtonLabel: openButtonLabel
      })]
    })
  });
};
InlineSheet.Wrapper = InlineSheetWrapper;

var InlineSheetResponsive = function InlineSheetResponsive(props) {
  if (!props.responsive) {
    return jsx(InlineSheet, __assign({}, props));
  }
  // If the sheet is larger, use the regular sheet on a wider range of viewport sizes
  var breakpoint = props.size === 'lg' ? 'xl' : 'lg';
  // remove the props that are not applicable to the regular sheet, depending on breakpoints
  // eslint-disable-next-line @typescript-eslint/unbound-method
  props.openButtonLabel;
    props.showCollapsedSheet;
    props.responsive;
    var onToggle = props.onToggle,
    rest = __rest(props, ["openButtonLabel", "showCollapsedSheet", "responsive", "onToggle"]);
  return jsxs(Fragment, {
    children: [jsx(Responsive, {
      maxWidth: breakpoint,
      render: function () {
        return jsx(Sheet, __assign({}, rest, {
          onClose: function () {
            return onToggle === null || onToggle === void 0 ? void 0 : onToggle(false);
          }
        }));
      }
    }), jsx(Responsive, {
      minWidth: breakpoint,
      render: function () {
        return jsx(InlineSheet, __assign({}, props));
      }
    })]
  });
};
InlineSheetResponsive.Wrapper = InlineSheetWrapper;

export { InlineSheetResponsive as InlineSheet, SHEET_PLACEMENTS, SHEET_SIZES, SHEET_WIDTHS, Sheet };
